import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { apiClient } from '../api/client';
import { ChevronLeft } from 'lucide-react';

const ConsentPage: React.FC = () => {
  const [consentDecision, setConsentDecision] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const handleContinue = async () => {
    if (consentDecision !== 'agree') return;

    setLoading(true);
    try {
      await apiClient.patch('/api/auth/consent?has_consented=true');
      await refreshUser();
      
      const response = await apiClient.get('/api/auth/next-task');
      const { redirect, patient_id } = response.data;

      if (redirect === 'survey') {
        navigate('/survey');
      } else if (redirect === 'group_c_selection') {
        navigate('/group-c-selection');
      } else if (redirect === 'chart') {
        navigate(`/patient-chart/${patient_id}`);
      } else {
        navigate('/patient-chart/patient_0');
      }
    } catch (error) {
      console.error('Error updating consent:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-4xl space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Research Consent Form</h1>
          <div className="h-1 w-full bg-blue-500 rounded-full mb-6"></div>
          <p className="text-gray-600">
            Please review the research consent form below carefully before deciding whether to participate.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200" style={{ height: '600px' }}>
          <iframe
            src="/consent_document.pdf#toolbar=0"
            title="Consent Document"
            className="w-full h-full"
            frameBorder="0"
          />
        </div>

        <Card className="border-gray-200 shadow-md">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-lg font-semibold text-gray-800">Your Consent Decision</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <RadioGroup
              value={consentDecision || ''}
              onValueChange={setConsentDecision}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <div
                className={`flex items-center space-x-2 border rounded-md p-4 flex-1 cursor-pointer transition-colors ${
                  consentDecision === 'agree' ? 'bg-green-50 border-green-500' : 'hover:bg-gray-50 border-gray-200'
                }`}
                onClick={() => setConsentDecision('agree')}
              >
                <RadioGroupItem value="agree" id="agree" className="text-green-600 border-green-600" />
                <Label htmlFor="agree" className="flex-1 cursor-pointer font-medium text-green-700">
                  I agree to participate
                </Label>
              </div>

              <div
                className={`flex items-center space-x-2 border rounded-md p-4 flex-1 cursor-pointer transition-colors ${
                  consentDecision === 'disagree' ? 'bg-red-50 border-red-500' : 'hover:bg-gray-50 border-gray-200'
                }`}
                onClick={() => setConsentDecision('disagree')}
              >
                <RadioGroupItem value="disagree" id="disagree" className="text-red-600 border-red-600" />
                <Label htmlFor="disagree" className="flex-1 cursor-pointer font-medium text-red-700">
                  I do not agree to participate
                </Label>
              </div>
            </RadioGroup>

            <div className="mt-8 flex flex-col items-center">
              <Button
                onClick={handleContinue}
                disabled={consentDecision !== 'agree' || loading}
                className={`w-full max-w-xs py-6 text-lg font-semibold transition-all ${
                  consentDecision === 'agree' 
                    ? 'bg-blue-400 hover:bg-blue-500 text-white shadow-md' 
                    : 'bg-blue-200 text-blue-400 cursor-not-allowed'
                }`}
              >
                {loading ? 'Processing...' : 'Continue to Study'}
              </Button>

              <button
                onClick={handleBack}
                className="mt-6 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Study Information
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConsentPage;
